import type { Language } from './constants';

export type LocalizedText = Record<Language, string>;

export interface BaseCategory {
  id: string;
  name: LocalizedText;
  icon: string;
  /** Image shipped with the app; `icon` is the fallback. */
  image: string;
  /** Accent colour (border) and a darker variant used for text. */
  color: string;
  textColor: string;
}

export interface BaseSymbol {
  id: string;
  categoryId: string;
  text: LocalizedText;
  /** Emoji fallback, shown when the image is unavailable. */
  icon: string;
  /** Image shipped with the app (client/public/symbols, see SOURCES.md there). */
  image: string;
}

/** Path of a bundled symbol image (client/public/symbols). */
export const symbolImage = (name: string) => `/symbols/${name}`;

/**
 * Built-in categories, in display order. Ids are stable and are referenced by
 * personalised symbols, so they must never change.
 */
export const BASE_CATEGORIES: BaseCategory[] = [
  { id: 'basico', name: { pt: 'Básico', en: 'Basic' }, icon: '❤️', image: symbolImage('cat-basico.webp'), color: '#2196f3', textColor: '#1565c0' },
  { id: 'pessoas', name: { pt: 'Pessoas', en: 'People' }, icon: '👥', image: symbolImage('cat-pessoas.webp'), color: '#4caf50', textColor: '#2e7d32' },
  { id: 'emocoes', name: { pt: 'Emoções', en: 'Emotions' }, icon: '😊', image: symbolImage('cat-emocoes.webp'), color: '#ff9800', textColor: '#b45309' },
  { id: 'lugares', name: { pt: 'Lugares', en: 'Places' }, icon: '📍', image: symbolImage('cat-lugares.webp'), color: '#9c27b0', textColor: '#7b1fa2' },
  { id: 'perguntas', name: { pt: 'Perguntas', en: 'Questions' }, icon: '❓', image: symbolImage('cat-perguntas.webp'), color: '#f44336', textColor: '#c62828' },
  { id: 'acoes', name: { pt: 'Ações', en: 'Actions' }, icon: '🏃', image: symbolImage('cat-acoes.webp'), color: '#009688', textColor: '#00695c' },
  { id: 'comida', name: { pt: 'Comida', en: 'Food' }, icon: '🍎', image: symbolImage('cat-comida.webp'), color: '#e91e63', textColor: '#ad1457' },
  { id: 'atividades', name: { pt: 'Atividades', en: 'Activities' }, icon: '⚽', image: symbolImage('cat-atividades.webp'), color: '#8bc34a', textColor: '#4d7c0f' },
  { id: 'tempo', name: { pt: 'Tempo', en: 'Time' }, icon: '📅', image: symbolImage('cat-tempo.webp'), color: '#3f51b5', textColor: '#303f9f' },
  { id: 'brinquedos', name: { pt: 'Brinquedos', en: 'Toys' }, icon: '🧸', image: symbolImage('cat-brinquedos.webp'), color: '#ffc107', textColor: '#a16207' },
];

type Row = [id: number, pt: string, en: string, icon: string, ext: 'webp' | 'svg'];

const ROWS: Record<string, Row[]> = {
  basico: [
    [1, 'Eu quero', 'I want', '❤️', 'webp'],
    [2, 'Sim', 'Yes', '✅', 'webp'],
    [3, 'Não', 'No', '❌', 'webp'],
    [4, 'Por favor', 'Please', '🙏', 'webp'],
    [5, 'Obrigado', 'Thank you', '😊', 'webp'],
    [6, 'Desculpa', 'Sorry', '😔', 'webp'],
    [7, 'Ajuda', 'Help', '🆘', 'svg'],
    [8, 'Mais', 'More', '➕', 'webp'],
    [9, 'Acabou', 'Finished', '✋', 'webp'],
    [10, 'Parar', 'Stop', '🛑', 'webp'],
  ],
  acoes: [
    [11, 'Comer', 'Eat', '🍽️', 'webp'],
    [12, 'Beber', 'Drink', '🥤', 'webp'],
    [13, 'Dormir', 'Sleep', '😴', 'webp'],
    [14, 'Brincar', 'Play', '🎮', 'webp'],
    [15, 'Ir', 'Go', '🚶', 'webp'],
    [16, 'Ver', 'See', '👀', 'webp'],
    [17, 'Ouvir', 'Listen', '👂', 'webp'],
    [18, 'Falar', 'Talk', '💬', 'webp'],
    [19, 'Ler', 'Read', '📖', 'webp'],
    [20, 'Escrever', 'Write', '✏️', 'webp'],
    // Daily routine actions (added later: new ids at the end keep the earlier ones in place).
    [101, 'Acordar', 'Wake up', '⏰', 'webp'],
    [102, 'Lavar as mãos', 'Wash hands', '🧼', 'webp'],
    [103, 'Escovar os dentes', 'Brush teeth', '🪥', 'webp'],
    [104, 'Tomar banho', 'Take a bath', '🛀', 'webp'],
    [105, 'Vestir a roupa', 'Get dressed', '👕', 'webp'],
    [106, 'Calçar o sapato', 'Put on shoes', '👟', 'webp'],
    [107, 'Tomar café', 'Have breakfast', '🥣', 'webp'],
    [108, 'Almoçar', 'Have lunch', '🍛', 'webp'],
    [109, 'Jantar', 'Have dinner', '🍲', 'webp'],
    [110, 'Esperar', 'Wait', '⏳', 'webp'],
    [111, 'Guardar', 'Put away', '📦', 'webp'],
    [112, 'Voltar', 'Come back', '↩️', 'webp'],
  ],
  emocoes: [
    [21, 'Feliz', 'Happy', '😊', 'webp'],
    [22, 'Triste', 'Sad', '😢', 'webp'],
    [23, 'Com raiva', 'Angry', '😠', 'webp'],
    [24, 'Com medo', 'Scared', '😨', 'webp'],
    [25, 'Surpreso', 'Surprised', '😲', 'webp'],
    [26, 'Cansado', 'Tired', '😴', 'webp'],
    [27, 'Animado', 'Excited', '🤩', 'webp'],
    [28, 'Confuso', 'Confused', '😕', 'webp'],
    [29, 'Calmo', 'Calm', '😌', 'webp'],
    [30, 'Preocupado', 'Worried', '😟', 'webp'],
  ],
  pessoas: [
    [31, 'Mamãe', 'Mom', '👩', 'webp'],
    [32, 'Papai', 'Dad', '👨', 'webp'],
    [33, 'Irmão', 'Brother', '👦', 'webp'],
    [34, 'Irmã', 'Sister', '👧', 'webp'],
    [35, 'Vovô', 'Grandpa', '👴', 'webp'],
    [36, 'Vovó', 'Grandma', '👵', 'webp'],
    [37, 'Amigo', 'Friend', '🤝', 'webp'],
    [38, 'Professor', 'Teacher', '🧑‍🏫', 'webp'],
    [39, 'Médico', 'Doctor', '🧑‍⚕️', 'webp'],
    [40, 'Eu', 'Me', '🙋', 'webp'],
  ],
  comida: [
    [41, 'Maçã', 'Apple', '🍎', 'webp'],
    [42, 'Banana', 'Banana', '🍌', 'webp'],
    [43, 'Pão', 'Bread', '🍞', 'webp'],
    [44, 'Leite', 'Milk', '🥛', 'webp'],
    [45, 'Água', 'Water', '💧', 'webp'],
    [46, 'Biscoito', 'Cookie', '🍪', 'webp'],
    [47, 'Pizza', 'Pizza', '🍕', 'webp'],
    [48, 'Sorvete', 'Ice cream', '🍦', 'webp'],
    [49, 'Suco', 'Juice', '🧃', 'webp'],
    [50, 'Chocolate', 'Chocolate', '🍫', 'webp'],
  ],
  atividades: [
    [51, 'Desenhar', 'Draw', '🎨', 'webp'],
    [52, 'Música', 'Music', '🎵', 'webp'],
    [53, 'TV', 'TV', '📺', 'webp'],
    [54, 'Livro', 'Book', '📚', 'webp'],
    [55, 'Bola', 'Ball', '⚽', 'webp'],
    [56, 'Carro', 'Car', '🚗', 'webp'],
    [57, 'Banheiro', 'Bathroom', '🚽', 'webp'],
    [58, 'Banho', 'Bath', '🛁', 'webp'],
    [59, 'Telefone', 'Phone', '📱', 'webp'],
    [60, 'Computador', 'Computer', '💻', 'webp'],
  ],
  lugares: [
    [61, 'Casa', 'Home', '🏠', 'webp'],
    [62, 'Escola', 'School', '🏫', 'webp'],
    [63, 'Hospital', 'Hospital', '🏥', 'webp'],
    [64, 'Parque', 'Park', '🌳', 'webp'],
    [65, 'Supermercado', 'Supermarket', '🛒', 'webp'],
    [66, 'Farmácia', 'Pharmacy', '💊', 'webp'],
    [67, 'Restaurante', 'Restaurant', '🍽️', 'webp'],
    [68, 'Praia', 'Beach', '🏖️', 'webp'],
    [69, 'Shopping', 'Mall', '🛍️', 'webp'],
    [70, 'Igreja', 'Church', '⛪', 'webp'],
  ],
  tempo: [
    [71, 'Segunda-feira', 'Monday', '📅', 'svg'],
    [72, 'Terça-feira', 'Tuesday', '📅', 'svg'],
    [73, 'Quarta-feira', 'Wednesday', '📅', 'svg'],
    [74, 'Quinta-feira', 'Thursday', '📅', 'svg'],
    [75, 'Sexta-feira', 'Friday', '📅', 'svg'],
    [76, 'Sábado', 'Saturday', '🗓️', 'svg'],
    [77, 'Domingo', 'Sunday', '🗓️', 'svg'],
    [78, 'Ontem', 'Yesterday', '⬅️', 'svg'],
    [79, 'Amanhã', 'Tomorrow', '➡️', 'svg'],
    [80, 'Hoje', 'Today', '📆', 'svg'],
  ],
  brinquedos: [
    [81, 'Bola', 'Ball', '⚽', 'webp'],
    [82, 'Boneca', 'Doll', '🪆', 'webp'],
    [83, 'Carrinho', 'Toy car', '🚙', 'webp'],
    [84, 'Quebra-cabeça', 'Puzzle', '🧩', 'webp'],
    [85, 'Blocos', 'Blocks', '🧱', 'webp'],
    [86, 'Urso de pelúcia', 'Teddy bear', '🧸', 'webp'],
    [87, 'Avião', 'Airplane', '✈️', 'webp'],
    [88, 'Trem', 'Train', '🚂', 'webp'],
    [89, 'Livro', 'Book', '📚', 'webp'],
    [90, 'Lápis de cor', 'Crayons', '🖍️', 'webp'],
  ],
  perguntas: [
    [91, 'Como?', 'How?', '🤔', 'svg'],
    [92, 'Qual?', 'Which?', '❓', 'svg'],
    [93, 'Por quê?', 'Why?', '❓', 'svg'],
    [94, 'Quantos?', 'How many?', '🔢', 'svg'],
    [95, 'O que aconteceu?', 'What happened?', '😮', 'webp'],
    [96, 'Onde?', 'Where?', '📍', 'svg'],
    [97, 'Onde está?', 'Where is it?', '🔍', 'webp'],
    [98, 'Quando?', 'When?', '🕐', 'svg'],
    [99, 'Quem?', 'Who?', '👤', 'svg'],
    [100, 'O quê?', 'What?', '❓', 'svg'],
  ],
};

export const BASE_SYMBOLS: BaseSymbol[] = BASE_CATEGORIES.flatMap((category) =>
  (ROWS[category.id] ?? []).map(([id, pt, en, icon, ext]) => ({
    id: `base-${id}`,
    categoryId: category.id,
    text: { pt, en },
    icon,
    image: symbolImage(`base-${id}.${ext}`),
  })),
);

export const BASE_CATEGORY_IDS = new Set(BASE_CATEGORIES.map((c) => c.id));


/**
 * Maps a built-in category name (as stored by the previous version of the app,
 * e.g. "Básico") to its stable id.
 */
export function baseCategoryIdFromName(name: string): string | undefined {
  const needle = name.trim().toLowerCase();
  return BASE_CATEGORIES.find(
    (c) => c.name.pt.toLowerCase() === needle || c.name.en.toLowerCase() === needle,
  )?.id;
}
